import React, { Component } from 'react';
import SectionHeading from "../../components/SectionHeading/SectionHeading";
import DropDown from "../../components/DropDown/DropDown";
import CheckboxDropDown from "../../components/CheckboxDropDown/CheckboxDropDown";
import { Link } from "react-router-dom";
import css from './Kpis.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import setFallbackAvatar from '../../utils/setFallbackAvatar';
import {timePeriodsThisQuarter, TIME_PERIOD_TODAY} from "../../helpers/timePeriods";
import shortenOfficeName from "../../utils/shortenOfficeName";
import _ from 'lodash';
import getDateQry from "../../helpers/dateHelpers";
import {YEAR_TO_DATE} from "../../helpers/timePeriods";
import YearTotalProgress from '../../components/YearTotalProgress/YearTotalProgress';


/**
 * @class
 *
 * KPIs page.
 */
const Kpis = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();
    timePeriodOptions = timePeriodsThisQuarter

    // because the names of table data properties differ for the aggregate facet ('reps' versus 'team' and 'office'),
    // it's convenient if we can update the names in a single area (the `aggregateButtonOnClick` handler), rather than having numerous conditionals:
    // DOORS_PROPERTY_NAME = 'knock';
    LEADS_PROPERTY_NAME = 'lead_count';
    SITS_PROPERTY_NAME = 'qs_count';
    PENDING_PROPERTY_NAME = 'psd_count';
    SALES_PROPERTY_NAME = 'csd_count';
    INSTALL_PROPERTY_NAME = 'icd_count';

    postRequestDataYearToDate = {
        fields: [
            'ContactMaster__c',
            'RepNameFormula__c',
            'Sales_Office__c',
            'Current_System_Size__c',
            'Deal_Value__c',
            'Pending_Sale_Date__c',
            'ContactMaster__r.Job_Title__c'
        ],
        where: {
            Fake__c: false,
            Void__c: false,
            "OppMaster__r.Fake__c": false,
            "ContactMaster__r.Job_Department__c": 'Sales'
        },
        date: getDateQry(YEAR_TO_DATE, 'Completed_Sale_Date__c')
    };

    // ---------- state: ----------
    state = {
        filterKeyword: '',
        kpisData: null,
        officeOptions: [],
        //Setting initial value of offices so that KPI totals calculate on first page load
        selectedOffices: ['V3 Redlands', 'V3 Rancho Cucamonga', 'V3 Victorville', 'V3 Lincoln',
            'V3 Granite Bay', 'V3 Granite Bay 2', 'V3 Inside Sales', 'V3 North Bay', 'V3 Inland Empire',
            'V3 El Dorado Hills', 'V3 Dublin', 'V3 Vacaville', 'V3 Austin', 'V3 Summer', 'V3 Inland Empire 2',
            'V3 Walnut Creek', 'V3 Temecula', 'V3 DFW', 'V3 East LA', 'V3 Orange County', 'V3 Sacramento'],
        selectedTimePeriodFilter: 'TODAY',
        selectedAggregateFacet: 'rep',
        sortBy: 'csd_count',

        // Table totals:
        // doorTotals: 0,
        leadsTotals: 0,
        sitsTotals: 0,
        pendingTotals: 0,
        salesTotals: 0,
        installTotals: 0,

        completed: 0,
        totalSales: 0
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        // fetch office list (for office filter):
        axios.get(
            '/sf/account/getOffices',
            {
                cancelToken: this.source.token
            })
            .then((res) => {

                // we need to map the office list to objects of labels and values for use in <CheckboxDropDown>:
                const offices = res.data.map(office => {
                    return {
                        label: office,
                        value: office,
                        checked: true // by default, have all office options selected
                    };
                });

                this.setState({
                    selectedOffices: offices.map(office => office.value),
                    officeOptions: offices
                });
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(GET) Error in KPIs: \n', err);
                }
            });

        axios.get(
            '/data/find/kpi',
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                const kpisData = res.data.cache;

                this.setState({
                    kpisData
                    // after setting the table data, we can calculate totals:
                }, () => {
                    this.calculateTotals();
                });
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(GET) Error in KPIs: \n', err);
                }
            });

        axios.post('/sf/repsaletracker/findNo',
            this.postRequestDataYearToDate,
            {
                cancelToken: this.source.token
            })
            .then((res) => {
                let yearSalesTotal = 0;
                const data = res.data.records;
                const filteredData = _.filter(data, function(o) {return o.Sales_Office__c.includes('V3')});
                
                _.each(filteredData, d => yearSalesTotal = yearSalesTotal + d.Deal_Value__c);
                
                let completed = 100 * (Math.round(yearSalesTotal) / 4000);

                this.setState({completed: Math.round(completed), totalSales: Math.round(yearSalesTotal)})
            })
            .catch(err => {
                if (axios.isCancel(err)) {
                    console.log('Request canceled');
                } else {
                    console.log('(POST) Error in Yearly Total: \n', err);
                }
            });
    }

    componentWillUnmount() {
        this.source.cancel();
    }

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf Kpis
     *
     * Handle filter input change when user enters keyword(s).
     * Filters the displayed table data by user keyword.
     */
    filterKeywordOnChange = (event) => {
        // prevent user from clicking before data has been loaded (would throw error):
        if (!this.state.kpisData) {
            return;
        }
        const filterKeyword = event.target.value.toLowerCase(); // make keyword case insensitive

        this.setState({ filterKeyword });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Filters table data by "time period".
     *
     * @param timePeriod {string}: The time-period option to filter by; time-periods options are found in `timePeriodOptions` list.
     */
    filterByTimePeriodOnChange = (timePeriod) => {
        // prevent user from clicking before data has been loaded (would throw error):
        if (!this.state.kpisData) {
            return;
        }

        this.setState({
            selectedTimePeriodFilter: timePeriod,

            // calculate totals after state has been updated:
        }, () => {
            this.calculateTotals();
        });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Filters table data by "office".
     *
     * @param optionList {array}: The update array that the CheckboxDropDown component returns -- same data structure as the options we pass to CheckboxDropDown.
     */
    filterByOfficeOnChange = (optionList) => {
        const selectedOffices = optionList
            .filter(item => item.checked)
            .map(item => item.value);

        this.setState({
            officeOptions: optionList,
            selectedOffices

            // calculate totals after state has been updated:
        }, () => {
            this.calculateTotals();
        });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Handle 'aggregate' button onClick.
     *
     * Displays the table data by given aggregate facet (i.e. "rep", "team", and "office");
     * also updates state to selected facet (the filtered table, the selected facet).
     *
     * @param facet {string}: Name of aggregate facet.
     */
    aggregateButtonOnClick = (facet) => {
        // prevent user from clicking before data has been loaded (would throw error):
        if (!this.state.kpisData) {
            return;
        }

        // these table data properties differ for 'rep' vs non-'rep':
        const LEADS_PROPERTY_NAME   = facet === 'rep' ? 'lead_count' : 'lead';
        const SITS_PROPERTY_NAME = facet === 'rep' ? 'qs_count' : 'qs';
        const PENDING_PROPERTY_NAME = facet === 'rep' ? 'psd_count' : 'psd';
        const SALES_PROPERTY_NAME   = facet === 'rep' ? 'csd_count' : 'csd';
        const INSTALL_PROPERTY_NAME   = facet === 'rep' ? 'icd_count' : 'icd';

        // because the sortBy state depends on the aggregate value, we need to update the sortBy property as well:
        let sortBy = this.state.sortBy;
        if (sortBy === 'lead_count' || sortBy === 'lead') {
            sortBy = facet === 'rep' ? 'lead_count' : 'lead';
        } else if (sortBy === 'qs_count' || sortBy === 'qs') {
            sortBy = facet === 'rep' ? 'qs_count' : 'qs';
        } else if (sortBy === 'psd_count' || sortBy === 'psd') {
            sortBy = facet === 'rep' ? 'psd_count' : 'psd';
        } else if (sortBy === 'csd_count' || sortBy === 'csd') {
            sortBy = facet === 'rep' ? 'csd_count' : 'csd';
        } else if (sortBy === 'icd_count' || sortBy === 'icd') {
            sortBy = facet === 'rep' ? 'icd_count' : 'icd';
        }

        // n.b. even though we're calling `sortByProperty` in the callback, we have to update the state (and class fields) for `displayedTable` here as well;
        // this is because `sortByProperty` only updates the displayed table, whereas this method takes a slice from the original kpisData:

        // update class fields:
        this.LEADS_PROPERTY_NAME = LEADS_PROPERTY_NAME;
        this.SITS_PROPERTY_NAME = SITS_PROPERTY_NAME;
        this.PENDING_PROPERTY_NAME = PENDING_PROPERTY_NAME;
        this.SALES_PROPERTY_NAME = SALES_PROPERTY_NAME;
        this.INSTALL_PROPERTY_NAME = INSTALL_PROPERTY_NAME;

        // update state:
        this.setState({
            selectedAggregateFacet: facet,
            sortBy,
        });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Handle 'sort by' button onClick.
     *
     * Updates state to the selected `sortBy` property.
     *
     * @param property {string}: property/table column to sort the table (descending order).
     */
    sortByProperty = (property) => {
        this.setState({
            sortBy: property
        });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Calculates the sum of the column values of the table.
     *
     * The sums/totals are displayed in the table header. N.b. we only want the calculations/summation of the table records
     * within the time period and office (i.e. rows/item filtered out are not included in the total).
     * Also note that totals is based on the data from the 'team' aggregate (regardless of the currently selected aggregate filter).
     */
    calculateTotals = () => {
        // prevent error being thrown via early return when data is still being fetched:
        if (!this.state.kpisData) {
            return;
        }

        // N.b. the totals is based on the 'team' aggregate, regardless of the currently selected aggregate filter:
        // copy original data (and slice the data's selected time period and aggregate):
        let table = _.cloneDeep(this.state.kpisData)['team'][this.state.selectedTimePeriodFilter];

        // filter by selected offices:
        table = _.filter(table, item => this.state.selectedOffices.includes(item.office));

        let leadsTotals = table.reduce((prev, curr) => {
            return prev + curr['lead'];
        }, 0);

        // To be updated
        let sitsTotals = table.reduce((prev, curr) => {
            return prev + curr['qs'];
        }, 0);

        let pendingTotals = table.reduce((prev, curr) => {
            return prev + curr['psd'];
        }, 0);

        let salesTotals = table.reduce((prev, curr) => {
            return prev + curr['csd'];
        }, 0);

        let installTotals = table.reduce((prev, curr) => {
            return prev + curr['icd'];
        }, 0);

        // format totals and update state:
        this.setState({
            leadsTotals:    this.formatTotal(leadsTotals),
            sitsTotals: this.formatTotal(sitsTotals),
            pendingTotals:  this.formatTotal(pendingTotals),
            salesTotals:    this.formatTotal(salesTotals),
            installTotals:    this.formatTotal(installTotals),
        });
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * The KPIs table we want to render -- after various sorting and filtering have been applied.
     *
     * @return {array}: KPIs Table after various filters, sorts, and aggregates have been applied.
     */
    displayedTable = () => {
        // prevent error being thrown via early return when data is still being fetched:
        if (!this.state.kpisData) {
            return [];
        }

        // copy original data (and slice the data's selected time period and aggregate):
        let displayedTable = _.cloneDeep(this.state.kpisData)[this.state.selectedAggregateFacet][this.state.selectedTimePeriodFilter];

        // apply various filters:
        displayedTable = displayedTable
            // filter by selected offices:
            .filter(item => this.state.selectedOffices.includes(item.office))

            // sort by current `sortBy` selection; if the values are equal,
            // then we sort by other KPI properties, in this order: Sales -> Pending -> Leads -> Doors
            .sort((a, b) => {
                // sort by current `sortBy` selection:
                if (a[this.state.sortBy] > b[this.state.sortBy]) {
                    return -1;
                } else if (a[this.state.sortBy] < b[this.state.sortBy])  {
                    return 1;
                } else {
                    // sort by sales:
                    if (a[this.SALES_PROPERTY_NAME] > b[this.SALES_PROPERTY_NAME]) {
                        return -1;
                    } else if (a[this.SALES_PROPERTY_NAME] < b[this.SALES_PROPERTY_NAME]) {
                        return 1;
                    }
                    else {
                        // sort by pending:
                        if ((a[this.PENDING_PROPERTY_NAME] > b[this.PENDING_PROPERTY_NAME])) {
                            return -1;
                        } else if ((a[this.PENDING_PROPERTY_NAME] < b[this.PENDING_PROPERTY_NAME])) {
                            return 1;
                        } else {
                            // sort by leads:
                            if ((a[this.LEADS_PROPERTY_NAME] > b[this.LEADS_PROPERTY_NAME])) {
                                return -1;
                            } else if ((a[this.LEADS_PROPERTY_NAME] < b[this.LEADS_PROPERTY_NAME])) {
                                return 1;
                            } else {
                                // sort by installs:
                                if ((a[this.INSTALL_PROPERTY_NAME] > b[this.INSTALL_PROPERTY_NAME])) {
                                    return -1;
                                } else if ((a[this.INSTALL_PROPERTY_NAME] < b[this.INSTALL_PROPERTY_NAME])) {
                                    return 1;
                                } else {
                                    return 1;
                                }
                            }
                        }
                    }
                }
            })
            // add ranking:
            // ranking number is independent of keyword filter (hence, we save keyword filter for last):
            .map((item, index) => {
                return {
                    ...item,
                    rank: index + 1
                };
            })
            // filter by keyword:
            .filter(item => {
                const itemName = item.name.toLowerCase();
                return itemName.includes(this.state.filterKeyword);
            });

        return displayedTable;
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Formats numbers in the table header's "totals" row.
     *
     * @param num {number}: Number to format -- the total for the specific table column.
     * @return {string|number}: Formatted integer.
     */
    formatTotal = (num) => {
        let _num = Math.ceil(num);

        // if larger than 1,0000 format string:
        if (_num >= 1000) {
            _num = ((_num / 1000).toFixed(1)) + 'K';
        }

        return _num;
    };

    // ---------- computed style/css properties: ----------
    /**
     * @method
     * @methodOf Kpis
     *
     * CSS classes for 'sort by' buttons.
     *
     * @param property {string}: The property/table column that the button sorts.
     * @return {string}: Generated css classes.
     */
    sortButtonCss = (property) => {
        return `btn flex-grow-1 mr-1 ${this.state.sortBy === property ? 'btn-primary' : 'btn-outline'}`;
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * CSS classes for aggregate buttons.
     *
     * @param facet {string}: Name of the aggregate facet.
     * @return {string}: Generated css classes.
     */
    aggregateButtonCss = (facet) => {
        return `btn flex-grow-1 mr-1 ${this.state.selectedAggregateFacet === facet ? 'btn-primary' : 'btn-outline'}`;
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * CSS classes for table's <td> and <tr>.
     *
     * @param property {string}: Name of 'sort by' property (e.g. 'sales', 'leads', etc.).
     * @return {string}: Generated css classes.
     */
    tableCellCss = (property) => {
        return this.state.sortBy === property ? css.Kpis__Table__Sorted : '';
    };

    /**
     * @method
     * @methodOf Kpis
     *
     * Inline styles for 'rank' text in table.
     *
     * Method reduces font size as the number of digits of the rank increase. We need to reduce the size of the text,
     * otherwise it will overflow. Conversely, simply setting a small font size is not good UX, especially since the
     * most important rows are near the top.
     *
     * @param index {number}: Array index, which corresponds to the sale rep's rank.
     * @return {object}: Style object.
     **/
    tableRankStyleObj = (index) => {
        let styleObj;

        if (index < 9) {
            styleObj = {fontSize: '12px'};
        } else if (index < 99) {
            styleObj = {fontSize: '11px', paddingRight: '3px'};
        } else {
            styleObj = {fontSize: '9px', paddingRight: '3px'};
        }

        return styleObj;
    };

    render() {
        return (
            <div className={css.Kpis}>

                <div className='d-flex align-items-center justify-content-center text-white text-uppercase font-weight-bold'>Road To 4000</div>
                <div><YearTotalProgress bgcolor="#00695c" completed={this.state.completed} total={this.state.totalSales} /></div>
                {/* TODO: need to not make heading always uppercase - because "s" in "KPIs" should be lowercase: */}
                <SectionHeading heading="KPIs" subheading="Refreshed Every 15 Minutes" />

                {/* ---------- facets/filters row: ---------- */}
                <div className="row">
                    <div className="col-12 col-sm-6">
                        <h4>Who</h4>
                        <div className="d-flex">
                            <button
                                className={this.aggregateButtonCss('rep')}
                                type="button"
                                onClick={() => this.aggregateButtonOnClick('rep')}
                            >Rep</button>
                            <button
                                className={this.aggregateButtonCss('team')}
                                type="button"
                                onClick={() => this.aggregateButtonOnClick('team')}
                            >team</button>
                            <button
                                className={this.aggregateButtonCss('office')}
                                type="button"
                                onClick={() => this.aggregateButtonOnClick('office')}
                            >Office</button>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 mt-3 mt-sm-0">
                        <h4>When</h4>
                        <DropDown
                            options={this.timePeriodOptions}
                            onDropdownSelect={(val) => this.filterByTimePeriodOnChange(val)}
                            default={TIME_PERIOD_TODAY}
                        />
                    </div>
                </div>

                {/* ---------- facets/filters row 2: ---------- */}
                <div className="row mt-4">
                    <div className="col-sm-12 col-md-6">
                        {/* TODO: On Leads and Customers, the sorting is done when user clicks on table header column.
                            TODO: We should replace buttons with sort on header click: */}
                        <h4>Sort</h4>
                        <div className="d-inline">
                            <button className={this.sortButtonCss(this.LEADS_PROPERTY_NAME)}
                                    type="button"
                                    onClick={() => this.sortByProperty(this.LEADS_PROPERTY_NAME)}
                            >Leads</button>
                            <button className={this.sortButtonCss(this.SITS_PROPERTY_NAME)}
                                    type="button"
                                    onClick={() => this.sortByProperty(this.SITS_PROPERTY_NAME)}
                            >QSits</button>
                            <button className={this.sortButtonCss(this.PENDING_PROPERTY_NAME)}
                                    type="button"
                                    onClick={() => this.sortByProperty(this.PENDING_PROPERTY_NAME)}
                            >Signed</button>
                            <button className={this.sortButtonCss(this.SALES_PROPERTY_NAME)}
                                    type="button"
                                    onClick={() => this.sortByProperty(this.SALES_PROPERTY_NAME)}
                            >Sales</button>
                            <button className={this.sortButtonCss(this.INSTALL_PROPERTY_NAME)}
                                    type="button"
                                    onClick={() => this.sortByProperty(this.INSTALL_PROPERTY_NAME)}
                            >Installs</button>
                        </div>
                    </div>
                    <div className="col-sm-12 col-md-6 mt-3 mt-md-0">
                        <h4>Where</h4>
                        <CheckboxDropDown
                            options={this.state.officeOptions}
                            onDropdownSelect={(options) => this.filterByOfficeOnChange(options)}
                            text="Office"
                        />
                    </div>
                </div>

                {/* ---------- facets/filters row 3: ---------- */}
                <div className="row mt-4">
                    <div className="col-sm-12">
                        <div className="input-with-icons-container">
                            <FontAwesomeIcon icon={faFilter} />
                            <input
                                className="input"
                                type="text"
                                placeholder="Filter Results"
                                value={this.state.filterKeyword}
                                onChange={(event) => this.filterKeywordOnChange(event)}
                            />
                        </div>
                    </div>
                </div>

                {/* ---------- table: ---------- */}
                <table className={`${css.Kpis__Table} mt-5`}>
                    <thead>
                    <tr>
                        <th colSpan="2">Sales Rep</th>
                        {/* <th className={this.tableCellCss(this.DOORS_PROPERTY_NAME)}><span>Doors</span></th> */}
                        <th className={this.tableCellCss(this.LEADS_PROPERTY_NAME)}><span>Leads</span></th>
                        <th className={this.tableCellCss(this.SITS_PROPERTY_NAME)}><span>QSits</span></th>
                        <th className={this.tableCellCss(this.PENDING_PROPERTY_NAME)}><span>Signed</span></th>
                        <th className={this.tableCellCss(this.SALES_PROPERTY_NAME)}><span>Sales</span></th>
                        <th className={this.tableCellCss(this.INSTALL_PROPERTY_NAME)}><span>Installs</span></th>
                    </tr>
                    </thead>
                    <tbody>
                    {
                        // ----- first row displays totals: -----
                        <tr>
                            <td colSpan="2">TOTALS</td>
                            {/* <td className={this.tableCellCss(this.DOORS_PROPERTY_NAME)}><h3>{ this.state.doorTotals }</h3></td> */}
                            <td className={this.tableCellCss(this.LEADS_PROPERTY_NAME)}><h3>{ this.state.leadsTotals }</h3></td>
                            <td className={this.tableCellCss(this.SITS_PROPERTY_NAME)}><h3>{ this.state.sitsTotals }</h3></td>
                            <td className={this.tableCellCss(this.PENDING_PROPERTY_NAME)}><h3>{ this.state.pendingTotals }</h3></td>
                            <td className={this.tableCellCss(this.SALES_PROPERTY_NAME)}><h3>{ this.state.salesTotals }</h3></td>
                            <td className={this.tableCellCss(this.INSTALL_PROPERTY_NAME)}><h3>{ this.state.installTotals }</h3></td>
                        </tr>
                    }
                    {
                        // ----- table rows: -----
                        this.displayedTable()
                            // map array to markup:
                            .map(rep => {
                                return (
                                    <tr key={rep.repId}>
                                        { /* N.b. because the sales rep images are in the `/public` folder, we can access them through root and we don't need `require()`: */ }
                                        { /* If Sales Rep image doesn't exist, show default avatar: */}
                                        <td>
                                            <div className={css.Kpis__Table__AvatarContainer}>
                                                <img src={`https://v3-portal-file-storage.s3.us-west-2.amazonaws.com/repPhotos/${rep.name}.jpg`} alt='Sales Rep Portrait' onError={(event) => setFallbackAvatar(event)} />
                                                {/* <img src={`/images/${rep.name}.jpg`} onError={(event) => setFallbackAvatar(event)} alt='Sales Rep Portrait' /> */}
                                                <span style={this.tableRankStyleObj(rep.rank)}>{rep.rank}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {
                                                this.state.selectedAggregateFacet === 'office' ?
                                                    <div>{rep.office}</div>
                                                    :
                                                    <>
                                                        <div><Link to={`/repStats/${rep.repId}`}>{rep.name}</Link></div>
                                                        <div className="text-sm text-uppercase mt-2 mt-sm-0">{shortenOfficeName(rep.office)}</div>
                                                    </>
                                            }
                                        </td>
                                        {/* <td className={this.tableCellCss(this.DOORS_PROPERTY_NAME)}>{ Math.round(rep[this.DOORS_PROPERTY_NAME]) }</td> */}
                                        <td className={this.tableCellCss(this.LEADS_PROPERTY_NAME)}>{ Math.round(rep[this.LEADS_PROPERTY_NAME]) }</td>
                                        <td className={this.tableCellCss(this.SITS_PROPERTY_NAME)}>{ Math.round(rep[this.SITS_PROPERTY_NAME]) }</td>
                                        <td className={this.tableCellCss(this.PENDING_PROPERTY_NAME)}>{ Math.round(rep[this.PENDING_PROPERTY_NAME]) }</td>
                                        <td className={this.tableCellCss(this.SALES_PROPERTY_NAME)}>{  Math.round(rep[this.SALES_PROPERTY_NAME]) }</td>
                                        <td className={this.tableCellCss(this.INSTALL_PROPERTY_NAME)}>{  Math.round(rep[this.INSTALL_PROPERTY_NAME]) }</td>
                                    </tr>);
                            })
                    }
                    </tbody>
                </table>
            </div>
        );
    };
};

export default Kpis;
